import {useEffect, useState} from "react";
import {useDispatch, useSelector} from "react-redux";
import {Link, useLocation, useNavigate} from "react-router-dom";
import {useTranslation} from "react-i18next";

import VLFModal from "@/components/Modal";
import QuickActions from "@/components/QuickActions";
import PlayerAvatar from "@/components/PlayerAvatar";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import ListItemIcon from "@mui/material/ListItemIcon";
import ListItemText from "@mui/material/ListItemText";
import MenuIcon from "@mui/icons-material/Menu";
import Drawer from "@mui/material/Drawer";
import VLFSelectNew from "@/components/Input/SelectNew";
import Button from "@mui/material/Button";
import LogoutIcon from "@mui/icons-material/Logout";

import {RosterAPI} from "@/api/roster";
import {visibleRosters} from "@/utils/functions";
import {MessagesAPI} from "@/api/messages";
import {MESSAGES_READ_EVENT} from "@/utils/messages";

import {Collapse} from "@mui/material";
import {ExpandLess, ExpandMore} from "@mui/icons-material";

import {logout, setSelectedMenuItem, setCurrentSeason} from "@/actions";

import {getInternalClubId} from "@/utils/constants";

import {AREAS, areaForPath, canRead, homePath} from "@/utils/access";

function SideMenu() {
	const dispatch = useDispatch();
	const location = useLocation();
	const {t} = useTranslation();

	const selectedMenuItem = useSelector((state) => state?.selectedMenuItem);
	const user = useSelector((state) => state?.user);
	const playerId = useSelector((state) => state?.user?.playerId);
	const seasons = useSelector((state) => state?.seasons);
	const seasonId = useSelector((state) => state?.season?.id);

	const navigate = useNavigate();

	const [openMobileMenu, setOpenMobileMenu] = useState(false);
	const [openHandleLogout, setOpenHandleLogout] = useState(false);
	const [openSections, setOpenSections] = useState({});
	const [teams, setTeams] = useState([]);
	const [unreadCount, setUnreadCount] = useState(0);

	const SECTION_BY_AREA = {
		[AREAS.DIREZIONE]: "direction",
		[AREAS.SEGRETERIA]: "direction",
		[AREAS.ANAGRAFICHE]: "squad",
		[AREAS.ATTIVITA]: "events",
		[AREAS.PERFORMANCE]: "performance",
		[AREAS.AREA_MEDICA]: "medical",
		[AREAS.SCOUTING]: "scouting",
		[AREAS.CONFIGURAZIONI]: "settings",
	};

	useEffect(() => {
		const section = SECTION_BY_AREA[areaForPath(location.pathname)];
		const inTeams = location.pathname.startsWith("/manager/teams");
		setOpenSections(
			section ? {[section]: true, ...(inTeams ? {teams: true} : {})} : {},
		);
	}, [location.pathname]); // eslint-disable-line react-hooks/exhaustive-deps

	const toggleSection = (key) =>
		setOpenSections((previous) =>
			key === "teams"
				? {squad: true, teams: !previous.teams}
				: previous[key]
					? {}
					: {[key]: true},
		);

	const normalize = (s) => s.normalize("NFC").trim().toLowerCase();
	const stripSlash = (p) => p.replace(/\/+$/, "");

	const getItemClassName = (path) => {
		const target = new URL(path, window.location.origin);
		const curPath = stripSlash(location.pathname);
		const targetPath = stripSlash(target.pathname);
		const targetParams = Array.from(target.searchParams.entries());

		const pathMatch =
			targetParams.length > 0
				? curPath === targetPath
				: curPath === targetPath || curPath.startsWith(targetPath + "/");

		if (!pathMatch) return "";
		if (targetParams.length === 0) return "selected";

		const curParams = new URLSearchParams(location.search);
		const paramsMatch = targetParams.every(([k, v]) => {
			const cur = curParams.get(k);
			return cur !== null && normalize(cur) === normalize(v);
		});
		return paramsMatch ? "selected" : "";
	};

	const getTeams = async () => {
		try {
			const response = await RosterAPI.list({
				seasonId,
				prospect: false,
				clubId: getInternalClubId(),
			});
			setTeams(
				visibleRosters(
					(response || []).filter(
						(t) =>
							!t.prospect && (!t.clubId || t.clubId === getInternalClubId()),
					),
				),
			);
		} catch {
			// silently ignore
		}
	};

	useEffect(() => {
		if (seasonId) getTeams();
	}, [seasonId, location.pathname]); // eslint-disable-line react-hooks/exhaustive-deps

	useEffect(() => {
		const fetchUnread = async () => {
			try {
				const data = await MessagesAPI.getUnreadCount();
				setUnreadCount(data?.count ?? data ?? 0);
			} catch {
				// silently ignore
			}
		};
		fetchUnread();
		const interval = setInterval(fetchUnread, 30000);
		window.addEventListener(MESSAGES_READ_EVENT, fetchUnread);
		return () => {
			clearInterval(interval);
			window.removeEventListener(MESSAGES_READ_EVENT, fetchUnread);
		};
	}, []);

	// ── Atomic item renderer ──────────────────────────────────────────────────
	const getListItem = (listItem) => {
		const handleItemClick = () => {
			if (listItem.subMenu) {
				toggleSection(listItem.subMenuKey);
			} else {
				setOpenMobileMenu(false);
			}
		};
		return (
			<>
				<ListItem
					className={
						getItemClassName(listItem.path) +
						(!listItem.imagePath ? " only-text" : "")
					}
					button
					component={listItem.subMenu ? "div" : Link}
					to={!listItem.subMenu ? listItem.path : undefined}
					onClick={handleItemClick}
				>
					<ListItemIcon>
						<img
							className={
								"item-icon" +
								(listItem.imageClassName ? " " + listItem.imageClassName : "")
							}
							src={listItem.imagePath}
							alt=""
						/>
					</ListItemIcon>
					<ListItemText primary={listItem.label} />
					{listItem.badge ? (
						<span className="sidemenu-badge">{listItem.badge}</span>
					) : null}
					{listItem.subMenu &&
						(openSections[listItem.subMenuKey] ? (
							<ExpandLess />
						) : (
							<ExpandMore />
						))}
				</ListItem>

				{listItem.subMenu && openSections[listItem.subMenuKey] && (
					<List component="div" disablePadding>
						{listItem.subMenu.map((subItem, index) => (
							<ListItem
								key={index}
								className={getItemClassName(subItem.path)}
								button
								component={Link}
								to={subItem.path}
								onClick={() => setOpenMobileMenu(false)}
							>
								<ListItemIcon>
									<img className="item-icon" src={subItem.imagePath} alt="" />
								</ListItemIcon>
								<ListItemText primary={subItem.label} />
							</ListItem>
						))}
					</List>
				)}
			</>
		);
	};

	// ── Collapsible section component ─────────────────────────────────────────
	const CollapsibleSection = ({sectionKey, label, icon, children}) => (
		<>
			<ListItem
				button
				className={
					(sectionKey === "teams" ? "menu-subsection" : "menu-macroarea") +
					(openSections[sectionKey] ? " is-open" : "")
				}
				aria-expanded={!!openSections[sectionKey]}
				onClick={() => toggleSection(sectionKey)}
			>
				<ListItemIcon>
					<img className="item-icon" src={icon} alt="" />
				</ListItemIcon>
				<ListItemText primary={label} />
				{openSections[sectionKey] ? <ExpandLess /> : <ExpandMore />}
			</ListItem>
			<Collapse in={!!openSections[sectionKey]} timeout="auto" unmountOnExit>
				<List component="div" disablePadding>
					{children}
				</List>
			</Collapse>
		</>
	);

	// ── Season selector ───────────────────────────────────────────────────────
	const formattedSeasons = seasons.map((s) => ({value: s.id, label: s.name}));
	const selectedSeason = formattedSeasons.find((s) => s.value === seasonId);

	const seasonSelectStyles = {
		control: (provided, state) => ({
			...provided,
			marginTop: "0px",
			marginBottom: "0px",
			minHeight: "40px",
			border: "1px solid var(--sidebar-line)",
			borderRadius: "var(--radius)",
			backgroundColor: "var(--sidebar-surface)",
			boxShadow: state.isFocused
				? "0 0 0 2px var(--color-primary-alpha-40)"
				: "none",
			"&:hover": {borderColor: "var(--sidebar-text-muted)"},
		}),
		valueContainer: (provided) => ({...provided, padding: "0 14px"}),
		placeholder: (provided) => ({
			...provided,
			color: "var(--sidebar-text-muted)",
		}),
		singleValue: (provided) => ({
			...provided,
			color: "var(--color-surface)",
			fontWeight: 600,
		}),
		menu: (provided) => ({
			...provided,
			borderRadius: "14px",
			overflow: "hidden",
		}),
		option: (provided, state) => ({
			...provided,
			backgroundColor: state.isSelected
				? "var(--color-primary-alpha-15)"
				: state.isFocused
					? "var(--color-primary-alpha-8)"
					: "transparent",
			color: "var(--color-text)",
		}),
	};

	const seasonsSelect = (
		<div className="season-card">
			<div className="season-card__header">
				<div className="season-card__label">Stagione attiva</div>
				<div className="season-card__current">
					{selectedSeason?.label || "—"}
				</div>
			</div>
			<VLFSelectNew
				classNamePrefix="season-select"
				containerStyles={{width: "100%"}}
				styles={seasonSelectStyles}
				options={formattedSeasons}
				isClearable={false}
				sortOrder="descend"
				value={selectedSeason}
				placeholder="Scegli stagione"
				onChange={(item) => {
					if (!item) return;
					dispatch(
						setCurrentSeason({
							season: seasons?.find((s) => s.id === item.value),
						}),
					);
					localStorage.setItem("seasonId", item.value);
					window.location.href = "/";
				}}
			/>
		</div>
	);

	// ── User info block ───────────────────────────────────────────────────────
	const getUserInfo = () => (
		<div className="user-info-container">
			<div
				className="d-flex align-items-center user-info-link"
				role="button"
				tabIndex={0}
				title="Il mio account"
				onClick={() => navigate("/account")}
				onKeyDown={(e) => e.key === "Enter" && navigate("/account")}
			>
				<PlayerAvatar
					firstName={user?.firstName}
					lastName={user?.lastName}
					name={user?.userName}
					size="small"
				/>
				<div className="user-container d-flex flex-column">
					<div className="user-data">
						{[user?.firstName, user?.lastName].filter(Boolean).join(" ") ||
							user?.userName}
					</div>
					<div className="user-role">{user?.roleName}</div>
				</div>
			</div>
			<Button
				variant="outlined"
				color="primary"
				size="small"
				onClick={() => setOpenHandleLogout(true)}
			>
				<LogoutIcon />
			</Button>
		</div>
	);

	// ── Icons shorthand ───────────────────────────────────────────────────────
	const icons = {
		business: require("@/assets/images/manager/business.png"),
		club: require("@/assets/images/manager/club.png"),
		player: require("@/assets/images/manager/movement_player.png"),
		playerAlt: require("@/assets/images/manager/player.png"),
		staff: require("@/assets/images/manager/staff.png"),
		football: require("@/assets/images/manager/football.png"),
		calendar: require("@/assets/images/manager/calendar.png"),
		match: require("@/assets/images/coach/match.png"),
		training: require("@/assets/images/coach/training.png"),
		exercise: require("@/assets/images/manager/exercise.png"),
		tqr: require("@/assets/images/coach/tqr.png"),
		rpe: require("@/assets/images/coach/rpe.png"),
		vas: require("@/assets/images/coach/muscle-pain.png"),
		test: require("@/assets/images/coach/test.png"),
		evaluation: require("@/assets/images/manager/evaluation.png"),
		scoutSearch: require("@/assets/images/scout/search.png"),
		scoutAlert: require("@/assets/images/scout/alert.png"),
		scoutArchive: require("@/assets/images/scout/archive.png"),
		target: require("@/assets/images/manager/target.png"),
		target2: require("@/assets/images/manager/target2.png"),
		medical: require("@/assets/images/medical/record.png"),
		video: require("@/assets/images/manager/video.png"),
		settings: require("@/assets/images/manager/settings.png"),
		category: require("@/assets/images/manager/category.png"),
		performance: require("@/assets/images/manager/performance.png"),
		logoPlayer: require("@/assets/images/old/logo_player.png"),
		user: require("@/assets/images/user.png"),
	};

	// Passaggio tra menu completo e vista essenziale DS
	const switchView = (view) => {
		dispatch(setSelectedMenuItem(view));
		setOpenMobileMenu(false);
		navigate(view === "ds" ? "/ds/oggi" : homePath(user, true));
	};

	const getViewSwitch = (view, label) => (
		<ListItem
			button
			className="menu-view-switch"
			onClick={() => switchView(view)}
		>
			<ListItemIcon>
				<img className="item-icon" src={icons.settings} alt="" />
			</ListItemIcon>
			<ListItemText primary={label} />
		</ListItem>
	);

	// ── Menu ────────────────────────────────────────────────────────────────

	// Menu completo: una sezione per area, visibile con almeno la sola lettura
	const can = (area) => canRead(user, area);

	const clubMenu = (
		<List className="side-menu-list">
			{can(AREAS.DIREZIONE) && getViewSwitch("ds", "Vista essenziale DS")}

			{(can(AREAS.DIREZIONE) || can(AREAS.SEGRETERIA)) && (
				<CollapsibleSection
					sectionKey="direction"
					label="Direzione"
					icon={icons.business}
				>
					{can(AREAS.SEGRETERIA) &&
						getListItem({
							path: "/secretariat",
							imagePath: icons.business,
							label: "Segreteria",
						})}
					{can(AREAS.DIREZIONE) && (
						<>
							{getListItem({
								path: "/manager/players-contracts",
								imagePath: icons.business,
								label: "Contratti",
							})}
							{getListItem({
								path: "/manager/finance",
								imagePath: icons.business,
								label: "Finanza",
							})}
							{getListItem({
								path: "/manager/market-simulation",
								imagePath: icons.target,
								label: "Simulazione mercato",
							})}
						</>
					)}
				</CollapsibleSection>
			)}

			{can(AREAS.ANAGRAFICHE) && (
				<CollapsibleSection
					sectionKey="squad"
					label="Squadre"
					icon={icons.club}
				>
					<CollapsibleSection sectionKey="teams" label="Rose" icon={icons.club}>
						{getListItem({
							path: "/manager/teams",
							imagePath: icons.club,
							label: "Gestione rose",
						})}
						{teams.map((team, k) => (
							<div key={k}>
								{getListItem({
									path: "/manager/teams/" + team.id,
									label: team.name || team.playerCategory?.name,
								})}
							</div>
						))}
					</CollapsibleSection>
					{getListItem({
						path: "/players",
						imagePath: icons.player,
						label: "Giocatori",
					})}
					{getListItem({
						path: "/manager/players-comparison",
						imagePath: icons.player,
						label: "Confronta giocatori",
					})}
					{getListItem({
						path: "/manager/staffs",
						imagePath: icons.staff,
						label: "Staff",
					})}
				</CollapsibleSection>
			)}

			{can(AREAS.ATTIVITA) && (
				<CollapsibleSection
					sectionKey="events"
					label="Attività"
					icon={icons.football}
				>
					{getListItem({
						path: "/match-day",
						imagePath: icons.football,
						label: "Giorno gara",
					})}
					{getListItem({
						path: "/calendar",
						imagePath: icons.calendar,
						label: "Calendario",
					})}
					{getListItem({
						path: "/exercises",
						imagePath: icons.training,
						label: "Esercizi",
					})}
					{getListItem({
						path: "/competitions",
						imagePath: icons.calendar,
						label: "Competizioni",
					})}
				</CollapsibleSection>
			)}

			{can(AREAS.PERFORMANCE) && (
				<CollapsibleSection
					sectionKey="performance"
					label="Performance"
					icon={icons.performance}
				>
					{getListItem({
						path: "/measurations/wellness",
						imagePath: icons.performance,
						label: "Benessere",
					})}
					{getListItem({
						path: "/gps",
						imagePath: icons.performance,
						label: "GPS",
					})}
					{getListItem({
						path: "/athletic-tests",
						imagePath: icons.test,
						label: "Test atletici",
					})}
				</CollapsibleSection>
			)}

			{can(AREAS.AREA_MEDICA) && (
				<CollapsibleSection
					sectionKey="medical"
					label="Area medica"
					icon={icons.medical}
				>
					{getListItem({
						path: "/medical/overview",
						imagePath: icons.medical,
						label: "Panoramica",
					})}
					{getListItem({
						path: "/medical/records",
						imagePath: icons.medical,
						label: "Cartelle",
					})}
				</CollapsibleSection>
			)}

			{can(AREAS.SCOUTING) && (
				<CollapsibleSection
					sectionKey="scouting"
					label="Scouting"
					icon={icons.scoutArchive}
				>
					{getListItem({
						path: "/scouting/planning",
						imagePath: icons.target,
						label: "Planning",
					})}
					{getListItem({
						path: "/scouting/players",
						imagePath: icons.scoutArchive,
						label: "Giocatori",
					})}
					{getListItem({
						path: "/scouting/relazioni",
						imagePath: icons.evaluation,
						label: "Relazioni",
					})}
					{getListItem({
						path: "/scouting/pipeline",
						imagePath: icons.target2,
						label: "Obiettivi",
					})}
					{getListItem({
						path: "/scouting/staffs",
						imagePath: icons.staff,
						label: "Tecnici",
					})}
					{getListItem({
						path: "/development/rosters",
						imagePath: icons.club,
						label: "Roster sviluppo",
					})}
				</CollapsibleSection>
			)}

			{getListItem({
				path: "/messages",
				imagePath: icons.staff,
				label: "Messaggi",
				badge: unreadCount > 0 ? unreadCount : null,
			})}

			{can(AREAS.CONFIGURAZIONI) && (
				<CollapsibleSection
					sectionKey="settings"
					label="Impostazioni"
					icon={icons.settings}
				>
					{getListItem({path: "/settings/users", label: "Utenti"})}
					{getListItem({
						path: "/settings/user-roles",
						label: "Ruoli e permessi",
					})}
					{getListItem({path: "/settings/clubs", label: "Club"})}
					{getListItem({path: "/settings/venues", label: "Campi"})}
					{getListItem({path: "/settings/seasons", label: "Stagioni"})}
					{getListItem({path: "/settings/categories", label: "Categorie"})}
					{getListItem({path: "/settings/competitions", label: "Competizioni"})}
					{getListItem({path: "/settings/modules", label: "Moduli tattici"})}
					{getListItem({
						path: "/settings/player-roles",
						label: "Ruoli giocatori",
					})}
					{getListItem({path: "/settings/staff-units", label: "Aree staff"})}
					{getListItem({path: "/settings/staff-roles", label: "Ruoli staff"})}
					{getListItem({
						path: "/settings/competition-seasons/import",
						label: "Import Json",
					})}
					{getListItem({path: "/import-data", label: "Import Wyscout"})}
					{getListItem({path: "/settings/components", label: "Componenti"})}
				</CollapsibleSection>
			)}
		</List>
	);

	// Vista essenziale DS: una voce per ogni area del club, solo sintesi
	const dsMenu = (
		<List className="side-menu-list">
			<li className="menu-group-label">Centro di controllo</li>
			{getListItem({
				path: "/ds/oggi",
				imagePath: icons.business,
				label: "Il club oggi",
			})}
			{getListItem({
				path: "/ds/attivita",
				imagePath: icons.scoutAlert,
				label: "Attività aperte",
			})}
			{getListItem({
				path: "/ds/timeline",
				imagePath: icons.calendar,
				label: "Linea temporale",
			})}
			<li className="menu-group-label">Area sportiva</li>
			{getListItem({
				path: "/ds/squadre",
				imagePath: icons.club,
				label: "Squadre",
			})}
			{getListItem({path: "/ds/gare", imagePath: icons.match, label: "Gare"})}
			{getListItem({
				path: "/ds/team-manager",
				imagePath: icons.training,
				label: "Team manager",
			})}
			{getListItem({
				path: "/ds/performance",
				imagePath: icons.performance,
				label: "Performance",
			})}
			{getListItem({
				path: "/ds/medica",
				imagePath: icons.medical,
				label: "Area medica",
			})}
			<li className="menu-group-label">Direzione</li>
			{getListItem({
				path: "/ds/segreteria",
				imagePath: icons.business,
				label: "Segreteria",
			})}
			{getListItem({
				path: "/ds/mercato",
				imagePath: icons.target,
				label: "Mercato & contratti",
			})}
			{getListItem({
				path: "/ds/procuratori",
				imagePath: icons.business,
				label: "Procuratori",
			})}
			{getListItem({
				path: "/ds/scouting",
				imagePath: icons.scoutArchive,
				label: "Scouting",
			})}
			{getListItem({path: "/ds/staff", imagePath: icons.staff, label: "Staff"})}
			<li className="menu-group-label">Club</li>

			{getListItem({
				path: "/ds/economia",
				imagePath: icons.business,
				label: "Economia",
			})}
			{getListItem({
				path: "/ds/strutture",
				imagePath: icons.calendar,
				label: "Strutture & logistica",
			})}
			{getListItem({
				path: "/ds/commerciale",
				imagePath: icons.business,
				label: "Commerciale",
			})}
			<li className="menu-group-divider" />
			{getListItem({
				path: "/messages",
				imagePath: icons.staff,
				label: "Messaggi",
				badge: unreadCount > 0 ? unreadCount : null,
			})}
			{getViewSwitch("director", "Vista completa")}
		</List>
	);

	const playerMenu = (
		<List className="side-menu-list">
			{playerId &&
				getListItem({
					path: "/profile/" + playerId,
					imagePath: icons.playerAlt,
					label: t("profile"),
				})}

			<CollapsibleSection
				sectionKey="performance"
				label={t("performance")}
				icon={icons.performance}
			>
				{getListItem({
					path: "/measurations/tqr",
					imagePath: icons.tqr,
					label: t("recoveries"),
				})}
				{getListItem({
					path: "/measurations/rpe",
					imagePath: icons.rpe,
					label: t("rpe"),
				})}
				{getListItem({
					path: "/measurations/vas",
					imagePath: icons.vas,
					label: t("vas"),
				})}
			</CollapsibleSection>

			{getListItem({
				path: "/trainings",
				imagePath: icons.training,
				label: t("trainings"),
			})}
			{getListItem({
				path: "/matches",
				imagePath: icons.match,
				label: t("matches"),
			})}

			{getListItem({
				path: "/messages",
				imagePath: icons.staff,
				label: "Messaggi",
				badge: unreadCount > 0 ? unreadCount : null,
			})}

			{/* <CollapsibleSection sectionKey="video" label={t("video")} icon={icons.video}>
				{getListItem({path: "/video?type=Clip allenamento", label: "Clip allenamento"})}
				{getListItem({path: "/video?type=Clip gara", label: "Clip gara"})}
				{getListItem({path: "/video?type=Video tecnici", label: "Video tecnici"})}
			</CollapsibleSection> */}
		</List>
	);

	const menuByView = {
		club: clubMenu,
		ds: dsMenu,
		player: playerMenu,
	};

	// ── Main render ───────────────────────────────────────────────────────────
	const getList = (mobile = false) => {
		const list = menuByView[selectedMenuItem] ?? null;

		const shouldShowSeasonCard =
			selectedMenuItem === "club" || selectedMenuItem === "ds";

		const logoBlock = (
			<div className="sidebar-logo-block" onClick={() => navigate("/")}>
				<div className="sidebar-logo-img" />
			</div>
		);

		const wrappedList = list ? (
			<div
				className={`side-container ${!shouldShowSeasonCard ? "no-season-card" : ""}`}
			>
				{logoBlock}
				<div className="side-menu-scroll">
					{selectedMenuItem !== "ds" && (
						<QuickActions onNavigate={() => setOpenMobileMenu(false)} />
					)}
					{shouldShowSeasonCard && seasonsSelect}
					{list}
				</div>
				{!mobile && getUserInfo()}
			</div>
		) : null;

		if (mobile) {
			return (
				<div className="d-flex flex-column side-menu-mobile">
					{wrappedList}
					{getUserInfo()}
				</div>
			);
		}

		return wrappedList;
	};

	return (
		<>
			<div className="side-menu-container">
				<div
					className="sandwich"
					onClick={() => setOpenMobileMenu(!openMobileMenu)}
				>
					<MenuIcon />
				</div>
				<Drawer
					variant="temporary"
					anchor="left"
					open={openMobileMenu}
					onClose={() => setOpenMobileMenu(!openMobileMenu)}
				>
					{getList(true)}
				</Drawer>
				{getList()}
			</div>
			{openHandleLogout && (
				<VLFModal
					open={openHandleLogout}
					title="Logout"
					content={
						<div className="mt-2">
							Sei sicuro di voler effettuare il logout?
						</div>
					}
					confirm={() => {
						localStorage.setItem("token", "");
						setOpenHandleLogout(false);
						dispatch(logout());
					}}
					close={() => setOpenHandleLogout(false)}
					confirmLabel="Conferma"
				/>
			)}
		</>
	);
}

export default SideMenu;
